import { useState, useEffect, useCallback } from 'react';
import { wsManager, MSG_TYPES } from '../services/websocketManager';

export const useWebSocket = () => {
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState(null);
  const [reconnecting, setReconnecting] = useState(false);
  const [reconnectAttempt, setReconnectAttempt] = useState(0);

  // Setup WebSocket state listener
  useEffect(() => {
    // Register state change handler
    const unsubscribe = wsManager.onStateChange((state) => {
      if (state.isConnected !== undefined) {
        setIsConnected(state.isConnected);
      }
      if (state.error !== undefined) {
        setError(state.error);
      }
      if (state.reconnecting !== undefined) {
        setReconnecting(state.reconnecting);
      }
      if (state.reconnectAttempt !== undefined) {
        setReconnectAttempt(state.reconnectAttempt);
      }
    });

    // Connect on mount (wsManager will handle duplicate connection attempts)
    wsManager.connect();

    // Cleanup on unmount
    return () => {
      unsubscribe();
      // Don't disconnect the WebSocket here - let it persist
    };
  }, []);

  // Wrap wsManager methods for consistent API
  const connect = useCallback(() => {
    wsManager.connect();
  }, []);

  const disconnect = useCallback(() => {
    wsManager.disconnect();
  }, []);

  const sendMessage = useCallback((type, data = {}) => {
    return wsManager.sendMessage(type, data);
  }, []);

  const onMessage = useCallback((type, handler) => {
    return wsManager.onMessage(type, handler);
  }, []);

  const createRoom = useCallback((playerName) => {
    return wsManager.createRoom(playerName);
  }, []);

  const joinRoom = useCallback((roomCode, playerName) => {
    return wsManager.joinRoom(roomCode, playerName);
  }, []);

  const startGame = useCallback(() => {
    return wsManager.startGame();
  }, []);

  const selectCards = useCallback((lifeCards, defenseCard) => {
    return wsManager.selectCards(lifeCards, defenseCard);
  }, []);

  const attack = useCallback((targetPlayerId) => {
    return wsManager.attack(targetPlayerId);
  }, []);

  const changeDefense = useCallback((targetPlayerId) => {
    return wsManager.changeDefense(targetPlayerId);
  }, []);

  const leaveRoom = useCallback(() => {
    return wsManager.leaveRoom();
  }, []);

  const kickPlayer = useCallback((playerId) => {
    return wsManager.kickPlayer(playerId);
  }, []);

  const addAIPlayer = useCallback(() => {
    return wsManager.addAIPlayer();
  }, []);

  const removeAIPlayer = useCallback((aiPlayerId) => {
    return wsManager.removeAIPlayer(aiPlayerId);
  }, []);

  const sendPlayerMessage = useCallback((recipientId, text) => {
    return wsManager.sendPlayerMessage(recipientId, text);
  }, []);

  const rejoinSession = useCallback((sessionID, roomCode) => {
    return wsManager.rejoinSession(sessionID, roomCode);
  }, []);

  return {
    isConnected,
    error,
    reconnecting,
    reconnectAttempt,
    connect,
    disconnect,
    sendMessage,
    onMessage,
    createRoom,
    joinRoom,
    rejoinSession,
    startGame,
    selectCards,
    attack,
    changeDefense,
    leaveRoom,
    kickPlayer,
    addAIPlayer,
    removeAIPlayer,
    sendPlayerMessage,
    MSG_TYPES
  };
};

export default useWebSocket;